'use client';

/** FACTORY — same API as classic factory board: /api/factory/status build pipeline. */
import { useEffect, useState } from 'react';
import { Glass, StatusChip, EmptyState, mono, display } from '@/future/primitives';

type Phase = { id: number; name: string; status: string; details?: string };
type Build = { slug: string; domain: string; profile: string; mode: string; status: string; phases: Phase[] };

const PHASE_DOT: Record<string, string> = {
  done: 'var(--uif-good)', running: 'var(--uif-cy-interactive)', pending: 'rgba(255,255,255,.15)', failed: 'var(--uif-bad)',
};

export function FactorySurface() {
  const [builds, setBuilds] = useState<Build[] | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => fetch('/api/factory/status').then(r => r.json())
      .then(d => { if (live) setBuilds(d.builds || []); }).catch(() => live && setBuilds([]));
    load();
    const t = setInterval(load, 60_000);
    return () => { live = false; clearInterval(t); };
  }, []);

  if (builds === null) return <div className="pt-10 text-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t5)' }}>SYNCING FACTORY LINE…</div>;
  if (!builds.length) return <EmptyState what="FACTORY IDLE" action="No builds on the line." />;

  return (
    <div className="mx-auto max-w-4xl flex flex-col gap-3">
      {builds.map(b => (
        <Glass key={b.slug} className="p-5">
          <div className="flex items-center gap-3">
            <div style={{ ...display(30), color: 'var(--uif-t1)' }}>{b.domain || b.slug}</div>
            <StatusChip label={(b.status || 'queued').toUpperCase()} kind={b.status === 'running' ? 'good' : b.status === 'failed' ? 'bad' : 'dim'} />
            <div className="ml-auto" style={{ ...mono(16, '.24em'), color: 'var(--uif-t5)' }}>{b.profile ? `PROFILE ${b.profile} · ` : ''}{(b.mode || 'pending').toUpperCase()}</div>
          </div>
          <div className="mt-4 flex items-center gap-1.5">
            {(b.phases || []).map((ph, i) => (
              <div key={ph.id} className="flex items-center gap-1.5 flex-1" title={`${ph.name}: ${ph.status}${ph.details ? ' — ' + ph.details : ''}`}>
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                  background: PHASE_DOT[ph.status] || PHASE_DOT.pending,
                  boxShadow: ph.status === 'running' ? '0 0 8px var(--uif-cy-interactive)' : 'none',
                }} />
                <div className="truncate" style={{ ...mono(14, '.14em'), color: ph.status === 'done' ? 'var(--uif-t4)' : ph.status === 'running' ? 'var(--uif-t2)' : 'var(--uif-t6)' }}>
                  {ph.name.toUpperCase()}
                </div>
                {i < b.phases.length - 1 && <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,.07)' }} />}
              </div>
            ))}
          </div>
        </Glass>
      ))}
    </div>
  );
}
